import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/GuestList.css";
import { memo, useState } from "react";
import { Event, AccountData } from "@markit/common.types";
import { getVisibleGuestList } from "../../utils/eventUtils/eventUtils";
import { IoIosArrowForward } from "react-icons/io";
import { GuestList } from "../../screens/Event/GuestList";
import { useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import { useSelector } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import { userCurrentNumberOfTickets } from "../../utils/eventUtils/userTicketUtils";
import { useAccountSelector } from "../../hooks/useParametricSelector";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import ProfilePic from "../ProfilePic";

type GuestListSummaryProps = {
  event: Event;
  isConfirmation?: boolean;
};

export const GuestListSummary = memo(function GuestListSummaryFn(
  props: GuestListSummaryProps
) {
  const { event, isConfirmation } = props;
  const { accountData, loggedIn } = useSelector(getAccountState).account;
  const { theme } = useTheme();
  const userId = useAccountSelector();
  const [displayGuestList, setDisplayGuestList] = useState<boolean>(false);
  const [eventGuestAccountData, setEventGuestAccountData] = useState<
    AccountData[]
  >([]);
  const [numUserTickets, setNumUserTickets] = useState(0);

  useAsyncOnMount(async () => {
    const [guestList, userNumTickets] = await Promise.all([
      getVisibleGuestList(event.id, 7),
      loggedIn === LoginState.LOGGED_IN || userId
        ? userCurrentNumberOfTickets(event.id, userId || accountData.uid)
        : 0,
    ]);

    // Create dummy data if no guests exist and no tickets (min. 3)
    if (guestList.length < 3 && userNumTickets === 0) {
      guestList.push(
        ...Array(3 - guestList.length).fill({
          uid: "",
          fullName: "Guest",
        })
      );
    }

    setEventGuestAccountData(guestList);
    setNumUserTickets(userNumTickets);
  });

  return !event.hideResponses ? (
    <div className="ColumnNormal" style={{ gap: 14 }}>
      <span
        className={isConfirmation ? "NameAndNumber" : "bodySubtext"}
        style={isConfirmation ? theme.PrimaryText : { fontWeight: 600 }}
      >
        Guest List
      </span>
      {isConfirmation ? null : (
        <HorizontalDivider
          altMargin={0}
          altWidth={1}
          eventTheme={event.theme}
        />
      )}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <div
          className={`ColumnNormal ${numUserTickets === 0 ? "BlurFilter" : ""}`}
          style={{
            gap: 14,
            pointerEvents: numUserTickets === 0 ? "none" : "all",
          }}
        >
          <div className={"ColumnNormal"} style={{ gap: 14 }}>
            {eventGuestAccountData.slice(0, 3).map((guest) => (
              <div className="AlignedRowSpaced" key={guest.uid}>
                <div className="AlignedRow" style={{ gap: 14 }}>
                  <ProfilePic user={guest} size={38} />
                  <span className="sectionTitle" style={theme.PrimaryText}>
                    {guest.fullName}
                  </span>
                </div>
                <span className="sectionTitle" style={theme.PrimaryText}>
                  Going
                </span>
              </div>
            ))}
          </div>
          <div
            className="AlignedRowSpacedSelect"
            onClick={() => setDisplayGuestList(true)}
          >
            <span style={{ color: Colors.RED2, fontWeight: 500 }}>
              See full guest list
            </span>
            <IoIosArrowForward size={18} color={Colors.RED2} />
          </div>
        </div>
        {numUserTickets === 0 ? (
          <div
            className="AlignedRow"
            style={{
              justifyContent: "center",
              width: 196,
              height: 44,
              borderRadius: 12,
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              margin: "auto",
              gap: 4,
              backgroundColor: theme.NewPrimaryBG.backgroundColor,
              border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
            }}
          >
            <Icon
              icon="ion:lock-closed"
              height={16}
              style={theme.PrimaryText}
            />
            <span className="bodyMedium" style={theme.PrimaryText}>
              RSVP to see guest list
            </span>
          </div>
        ) : null}
      </div>
      {displayGuestList ? (
        <GuestList setDisplayGuestList={setDisplayGuestList} event={event} />
      ) : null}
    </div>
  ) : null;
});
